<template>
  <base-container>
    <b-alert v-if="donationSuccess" variant="success" show>
      <b-row no-gutters align-v="center" class="pl-2">
        <b-col cols="auto">
          <fa-icon id="donation-success-icon" class="mr-2" :icon="['far', 'check-circle']" size="2x" />
        </b-col>
        <b-col cols="auto">
          <p class="mb-0 h2">{{ $t(donationSuccess) }}</p>
        </b-col>
      </b-row>
    </b-alert>
    <h1 class="my-4 text-center text-lg font-weight-bold">{{ $t('views.payment.place') }}</h1>

    <b-row class="px-3">
      <b-col>
        <h2 class="text-dark h3">
          <strong>{{ $t('views.payment.shippingTo') }}</strong>
          <span class="sentry-mask">{{ petOwner.firstName + ' ' + petOwner.lastName }}</span>
          <br />
          {{ petOwnerMailingAddress ? petOwnerMailingAddress : petOwnerAddress }}
        </h2>
        <!-- <h3 class="text-dark mb-0"><strong>Estimated Delivery:</strong> Jan 11, 2021</h3> -->
      </b-col>
      <b-col cols="auto">
        <h3 class="m-0">
          <b-link :href="editDetailsLink">{{ $t('views.payment.editAddress') }}</b-link>
        </h3>
      </b-col>
    </b-row>

    <hr />
    <totals :show-donation="!!donationSuccess" />

    <div v-if="order.total > 0" class="my-3">
      <h2 class="mb-0">{{ $t('views.payment.info') }}</h2>
      <h3 class="text-dark mb-2">
        {{ editStripe ? $t('views.payment.creditDebit') : $t('views.payment.onFile') }}
      </h3>
      <template v-if="order.total > 0">
        <b-row v-if="editStripe ? null : petOwner.stripeProfile" no-gutters align-v="center" class="stripe-row sentry-mask">
          <b-col cols="auto">
            <b-img width="48px" :src="require('@/assets/creditCards/' + petOwner.stripeProfile.type + '.svg')" alt="" />
          </b-col>
          <b-col class="pl-3 font">
            {{
              $t('components.stripe.profile', [
                $t('components.stripe.' + petOwner.stripeProfile.type),
                petOwner.stripeProfile.suffix,
                petOwner.stripeProfile.expiryMonth,
                petOwner.stripeProfile.expiryYear,
              ])
            }}
          </b-col>
        </b-row>

        <dp-stripe-form
          v-else
          ref="stripeForm"
          class="sentry-mask"
          :public-key="order.city.paymentProcessor.publicKey"
          :show-icons="true"
          @completedForm="completedStripeForm"
        />
      </template>

      <b-button v-if="petOwner.stripeProfile" variant="link" class="p-0 mt-1" @click="toggleStripe">
        {{ editStripe ? $t('views.payment.useFile') : $t('views.payment.useDifferent') }}
      </b-button>
    </div>

    <!-- eslint-disable-next-line vue/no-v-html -->
    <p v-if="getStoredCreditCardMessage" class="mt-3 mb-0" v-html="getStoredCreditCardMessage"></p>

    <!-- eslint-disable-next-line vue/no-v-html -->
    <p v-if="getTermsOfServiceAndPrivacyPolicyOptInCopy" class="mt-3 mb-0" v-html="getTermsOfServiceAndPrivacyPolicyOptInCopy"></p>

    <b-form-checkbox v-if="getMarketingCommunicationOptInCopy" v-model="marketingCommunicationOptIn" class="mt-3">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <span v-html="getMarketingCommunicationOptInCopy"></span>
    </b-form-checkbox>

    <b-button type="submit" class="mt-3" variant="primary" block :disabled="payDisabled" @click="pay">
      {{ $t('views.payment.place') }}
      <div v-if="paymentLoading" role="status" class="fa-pulse float-right mt-1">
        <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
        <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
      </div>
    </b-button>
    <p v-if="paymentError && errorMessage" class="text-danger mt-3 mb-0">{{ errorMessage }}</p>
    <p v-else-if="paymentError" class="text-danger mt-3 mb-0">{{ $t('views.payment.error') }}</p>
  </base-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { DpStripeForm } from '@docupet/component-library';
import totals from '@/components/basket/Totals.vue';
import BaseContainer from '@/components/BaseContainer';

export default {
  components: {
    totals,
    DpStripeForm,
    BaseContainer,
  },
  props: {
    context: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      editStripe: false,
      stripeComplete: false,
      paymentLoading: false,
      paymentError: false,
      setDefaultPaymentMethod: false,
      autoRenewalConfig: {
        licenses: false,
      },
      marketingCommunicationOptIn: null,
    };
  },
  computed: {
    ...mapState({
      petOwner: (state) => state.petOwner.petOwner,
      order: (state) => state.order.order,
      errorMessage: (state) => state.order.pay.error,
      donationSuccess: (state) => state.order.donation.success,
      locale: (state) => state.locale,
    }),
    ...mapGetters({
      getSections: 'order/getSections',
      isValidToProceed: 'order/isValid',
      getScreenContext: 'screenContext/getContext',
    }),
    getScreenContext() {
      return this.context;
    },
    editDetailsLink() {
      return `${window.BASE_URL}/${this.locale}/account/full/account-app/settings/my-details/edit`;
    },
    payDisabled() {
      return !!((this.order.total > 0 && !this.stripeComplete && this.editStripe) || this.paymentLoading);
    },
    isLicensingOrder() {
      return this.order.items?.map((item) => item.product?.type === 'LICENCE')?.some((val) => !!val);
    },
    petOwnerMailingAddress() {
      return this.petOwner?.mailingAddress?.display;
    },
    petOwnerAddress() {
      return this.petOwner?.address?.display;
    },
    getTermsOfServiceAndPrivacyPolicyOptInCopy() {
      return this.getScreenContext?.termsOfServiceAndPrivacyPolicyOptInCopy;
    },
    getMarketingCommunicationOptInCopy() {
      return this.getScreenContext?.marketingCommunicationOptInCopy;
    },
    getStoredCreditCardMessage() {
      return this.getScreenContext?.storedCreditCardMessage;
    },
  },
  created() {
    if (!this.order.id || !this.isValidToProceed) {
      this.$router.push({ name: 'Basket' });
    }
    this.editStripe = !this.petOwner.stripeProfile;
    this.setDefaultPaymentMethod = !!this.petOwner?.stripeProfile;
    if (this.petOwner?.autoRenewals) {
      this.autoRenewalConfig = this.petOwner?.autoRenewals;
    }
  },
  methods: {
    toggleStripe() {
      this.stripeComplete = false;
      this.editStripe = !this.editStripe;
    },
    completedStripeForm(value) {
      this.stripeComplete = value;
    },
    pay() {
      // Check if auto-renewal was updated
      if (!this.petOwner?.autoRenewals || this.petOwner.autoRenewals?.licenses === this.autoRenewalConfig?.licenses) {
        this.autoRenewalConfig = null;
      }

      this.paymentLoading = true;
      this.paymentError = false;
      if (this.editStripe && this.order.total > 0) {
        // If a new credit card is provided and order cost something, we first need a stripe token
        this.$refs.stripeForm.getToken().then((token) => {
          if (token) {
            this.$store
              .dispatch('order/pay', {
                token,
                autoRenewalConfig: this.autoRenewalConfig,
                setDefaultPaymentMethod: this.setDefaultPaymentMethod,
                marketingCommunicationOptIn: this.marketingCommunicationOptIn || null,
              })
              .then((order) => {
                if (order) {
                  this.analyticsTrackPurchase(order).finally(() => this.$store.commit('order/SET_ORDER', order));
                }
              })
              .catch(() => {
                this.paymentError = true;
              })
              .finally(() => {
                this.paymentLoading = false;
              });
          } else {
            this.paymentLoading = false;
            this.paymentError = true;
          }
        });
      } else {
        // If no cost or using credit card profile
        this.$store
          .dispatch('order/pay', {
            token: null,
            autoRenewalConfig: this.autoRenewalConfig,
            setDefaultPaymentMethod: this.setDefaultPaymentMethod || !!this.petOwner.stripeProfile,
            marketingCommunicationOptIn: this.marketingCommunicationOptIn || null,
          })
          .then((order) => {
            if (order) {
              this.analyticsTrackPurchase(order).finally(() => this.$store.commit('order/SET_ORDER', order));
            }
          })
          .catch(() => {
            this.paymentError = true;
          })
          .finally(() => {
            this.paymentLoading = false;
          });
      }
    },
    analyticsTrackPurchase(order) {
      return Promise.race([
        Promise.allSettled([this.googleTrackPurchase(order), this.googleAdsTracking(order), this.fbTrackPurchase(order)]),
        new Promise((resolve) => setTimeout(resolve, 5000)),
      ]);
    },
    googleTrackPurchase(order) {
      return new Promise((resolve, reject) => {
        if (window.dataLayer && order) {
          window?.dataLayer?.push({
            ecommerce: null,
          });
          const items = [];
          order?.items?.forEach((item) => {
            if (item.parent) return;
            const _itemData = {
              quantity: 1,
              coupon: order?.discount?.code || '',
              currency: `${order?.city?.country || 'US'}D`,
              item_list_id: item?.product?.style?.id || '',
              item_list_name: item?.product?.style?.code?.toUpperCase() || '',
            };
            item?.children?.forEach((child) => {
              const _childData = {
                ..._itemData,
                item_id: child.product?.id || '',
                item_name: child.product?.name?.toUpperCase() || '',
                item_category: child.product?.type?.toUpperCase() || '',
                price: Number(child.subtotal || 0).toFixed(2),
              };
              if (['OTHER'].includes(child.product?.type) || (child.product?.type === 'CONNECTOR' && child.product?.id !== '207')) {
                items.push(_childData);
              }
              if (child.product?.type === 'DESIGNER_TAG' && child.keyChain) {
                items.push({
                  ..._childData,
                  item_id: `${item?.product?.id}_MATCHING_KEYCHAIN`,
                  item_name: `${item?.product?.id}_MATCHING_KEYCHAIN`,
                  item_category: 'MATCHING_KEYCHAIN',
                });
              }
            });
            if (item?.customTagOrderInfo || item?.customization) {
              items.push({
                ..._itemData,
                item_id: `${item?.product?.type?.toUpperCase()}_PERSONALIZATION`,
                item_name: `${item?.product?.type?.toUpperCase()}_PERSONALIZATION`,
                item_category: 'PERSONALIZATION',
                price: Number(item?.customization || 0).toFixed(2),
              });
            }
            items.push({
              ..._itemData,
              item_id: item?.product?.id || '',
              item_name: item?.product?.name?.toUpperCase() || '',
              item_category: item?.product?.type?.toUpperCase() || '',
              price: Number(item?.subtotal || 0).toFixed(2),
            });
          });
          const eventID = `${order.city.country}-${order.petOwner.id}-${order.id}`;
          const ecommerce = {
            transaction_id: eventID,
            value: Number(order?.total || 0).toFixed(2),
            tax: Number(order?.tax || 0).toFixed(2),
            shipping: Number(order?.shipping || 0).toFixed(2),
            currency: `${order?.city?.country || 'US'}D`,
            coupon: order?.discount?.code || '',
            items: items,
          };
          window.dataLayer.push({
            event: 'purchase',
            ecommerce: ecommerce,
          });
          resolve();
        } else {
          reject();
        }
      });
    },
    fbTrackPurchase(order) {
      const purchaseEvent = [];
      order.items?.forEach((item) => {
        const eventIDPrefix = `P${order.city.country === 'US' ? 'U' : 'C'}${order.petOwner.id}-${order.id}-`;
        const fields = {
          eventID: `${eventIDPrefix}${item.id}`,
          data: {
            content_category: 'Order',
            num_item: 1,
            value: item.total - item.discount,
            currency: order.city.country === 'US' ? 'USD' : 'CAD',
            city: order.city.name,
            state: order.city.province?.split('_')?.[1],
            country: order.city.country,
            content_ids: item.product.id,
            content_name: `${item.product.name}`,
            content_type: (() => {
              if (item.product.type === 'LICENCE') {
                return item.group.pet.licenses?.length === 0 ? 'New License' : 'Renewed Licence';
              }
              if (item.product.type === 'DONATION') {
                return 'Donation';
              }
              if (item.product.type === 'DESIGNER_TAG' && !item.product.municipalTag && !item.customTagOrderInfo) {
                return 'Tag';
              }
              if (item.product.type === 'CONNECTOR') {
                return 'Connector';
              }
              if (item.product.type === 'REPLACEMENT_TAG') {
                return 'Replacement Tag';
              }
              if (item.product.type === 'OTHER') {
                return 'Other';
              }
              if (item.product.type === 'VOICE_AND_SIGHT') {
                return 'Voice & Sight';
              }
              if (['COLLAR', 'LEASH'].includes(item.product.type)) {
                return 'Collar & Leash';
              }
              if (item.product.type === 'PET_ESSENTIALS') {
                return 'Pet Essentials';
              }
              return 'Unknown';
            })(),
            contents: [{ id: item.product.id, quantity: 1 }],
          },
        };
        if (item.product.type === 'DESIGNER_TAG' && item.customTagOrderInfo) {
          // Designer Tag Purchase Event
          const designerTagFields = JSON.parse(JSON.stringify(fields));
          designerTagFields.data.value = item.product.price - item.discount;
          designerTagFields.data.content_type = 'Tag';
          purchaseEvent.push(designerTagFields);
          // Personalization Purchase Event
          fields.eventID = `${eventIDPrefix}P`;
          fields.data.value = item.customization;
          fields.data.content_ids = 'P';
          fields.data.content_name = fields.data.content_type = 'Personalization';
          fields.data.contents = [{ id: 'P', quantity: 1 }];
          purchaseEvent.push(fields);
        } else {
          purchaseEvent.push(fields);
        }
      });
      purchaseEvent.map(({ eventID, data }) => window.fbq('track', 'Purchase', data, { eventID }));
      return Promise.resolve();
    },
    googleAdsTracking(order) {
      return new Promise((resolve, reject) => {
        if (window.dataLayer) {
          window.gtag = function () {
            window.dataLayer.push(arguments);
          };
          window.gtag('js', new Date());
          window.gtag('config', 'AW-322085171');
          const eventID = `${order?.city?.country}-${order?.petOwner?.id}-${order?.id}`;
          const fireAdConversion = function (conversionLabel, value) {
            window.gtag('event', 'conversion', {
              send_to: `AW-322085171/${conversionLabel}`,
              value: Number(value).toFixed(2),
              currency: `${order?.city?.country || 'US'}D`,
              transaction_id: eventID,
            });
          };
          order?.items?.forEach((item) => {
            if (item.product?.type === 'LICENCE') {
              fireAdConversion(item?.group?.pet?.licenses?.length === 0 ? 'VmAsCKKOuaQDELPCypkB' : 'XH6TCKWOuaQDELPCypkB', item.subtotal);
            }
            if (item.product?.type === 'DONATION') {
              fireAdConversion('F54JCK6OuaQDELPCypkB', item.subtotal);
            }
            if (item.product?.type === 'DESIGNER_TAG' && !item.product?.municipalTag) {
              fireAdConversion('zMFoCJaOuaQDELPCypkB', item.subtotal);
            }
            if (['COLLAR', 'LEASH'].includes(item.product?.type)) {
              fireAdConversion('-VCWCJmOuaQDELPCypkB', item.subtotal);
            }
            if (item.customization) {
              fireAdConversion('CmZjCJyOuaQDELPCypkB', item.customization);
            }
            if (item.product?.type === 'CONNECTOR') {
              fireAdConversion('vFJRCJ-OuaQDELPCypkB', item.subtotal);
            }
            if (item.product?.type === 'REPLACEMENT_TAG') {
              fireAdConversion('77JmCKiOuaQDELPCypkB', item.subtotal);
            }
            if (item.product?.type === 'VOICE_AND_SIGHT') {
              fireAdConversion('Qe9MCKuOuaQDELPCypkB', item.subtotal);
            }
            if (item.product?.type === 'OTHER') {
              fireAdConversion('9IVOCLiJ9qUDELPCypkB', item.subtotal);
            }
            if (item.product?.type === 'PET_ESSENTIALS') {
              fireAdConversion('lBX4CLn_p80DELPCypkB', item.subtotal);
            }
          });
          resolve();
        } else {
          reject();
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
#donation-success-icon {
  color: $dp-green;
  background-color: $dp-white;
  border-radius: 100%;
}
</style>
